/*
  ppb-anim.js — PEAKSPAN H2 · 8,000 PPB fill animation (SPEC-SITE "ANIM SPEC").

  Mount: a single element `[data-ps-anim]` (fallback: `#ps-anim-mount`, then a
  bare `#ps-anim` section — the script then appends inside it). The script
  builds its own DOM: video (or poster image) stage, big PPB counter,
  10-min timer, 4,000 PPB standard-mode marker, silver PEAK badge with
  heartbeat line, replay button and the mandatory compliance footnote.

  Premium bar: real product film, easeOutQuart counter (~6s), IntersectionObserver
  start-once, video pauses offscreen, prefers-reduced-motion => static final frame.
  Namespaced ps- throughout. No external requests.
*/
(function () {
  'use strict';

  var DURATION = 6000;          // main count 0 -> 8000 (ms)
  var PPB_MAX = 8000;
  var TIMER_MAX = 600;          // seconds shown (10:00), synced to same easing
  var MARKER_PPB = 4000;        // "Standard mode · 5 min" pulse point

  var reducedMotion = false;
  try {
    reducedMotion = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) { /* noop */ }

  function easeOutQuart(t) {
    t = Math.min(1, Math.max(0, t));
    return 1 - Math.pow(1 - t, 4);
  }

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      /* defer one tick: with a deferred script readyState is already
         'interactive' here, and calling fn() synchronously would construct
         PpbAnim before the prototype methods further down are evaluated */
      setTimeout(fn, 0);
    }
  }

  onReady(function () {
    var mount = document.querySelector('[data-ps-anim]') ||
                document.getElementById('ps-anim-mount') ||
                document.getElementById('ps-anim');
    if (!mount || mount.hasAttribute('data-ps-anim-built')) return;
    mount.setAttribute('data-ps-anim-built', '');
    build(mount);
  });

  /* ------------------------------------------------------------------ *
   * DOM scaffold
   * ------------------------------------------------------------------ */
  function build(mount) {
    var videoUrl = mount.getAttribute('data-ps-video') || '';
    var posterUrl = mount.getAttribute('data-ps-poster') || '';

    var mediaHtml;
    if (!reducedMotion && videoUrl) {
      mediaHtml =
        '<video class="ps-anim__video" autoplay muted loop playsinline preload="metadata"' +
        ' poster="' + posterUrl + '" src="' + videoUrl + '"' +
        ' role="img" aria-label="The H88 bottle filling with hydrogen-rich water as the concentration counter rises to 8,000+ PPB"></video>';
    } else {
      mediaHtml =
        '<img class="ps-anim__video" src="' + posterUrl + '" alt=""' +
        ' role="img" aria-label="The H88 bottle filling with hydrogen-rich water as the concentration counter rises to 8,000+ PPB">';
    }

    var root = document.createElement('div');
    root.className = 'ps-anim';
    root.innerHTML =
      '<div class="ps-anim__stage">' +
        mediaHtml +
        '<button type="button" class="ps-anim__replay" aria-label="Replay animation" hidden>' +
          '<svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true" fill="none">' +
            '<path d="M4 12a8 8 0 1 0 2.34-5.66" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>' +
            '<path d="M4 3.5V7h3.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>' +
          '</svg><span>Replay</span>' +
        '</button>' +
      '</div>' +
      '<div class="ps-anim__data">' +
        '<p class="ps-anim__eyebrow">High mode · live fill</p>' +
        '<p class="ps-anim__count" aria-live="off"><span class="ps-anim__count-num">0</span><span class="ps-anim__count-unit">PPB</span></p>' +
        '<p class="ps-anim__timer"><svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" fill="none"><circle cx="12" cy="13" r="8" stroke="currentColor" stroke-width="1.6"/><path d="M12 9v4l2.6 1.8M9.5 2.5h5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg><span class="ps-anim__timer-num">00:00</span><span class="ps-anim__timer-cap">/ 10:00 high mode</span></p>' +
        '<p class="ps-anim__marker"><span class="ps-anim__marker-dot" aria-hidden="true"></span>Standard mode · 5 min <strong>4,000+ PPB</strong></p>' +
        '<p class="ps-anim__badge" aria-hidden="true"><span class="ps-anim__badge-chip">' +
          '<svg class="ps-anim__badge-line" viewBox="0 0 64 20" width="46" height="15" aria-hidden="true" fill="none">' +
            '<path d="M1 11h14l4-7 6 13 5-16 5 10h28" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
          '</svg><span>PEAK</span></span>' +
        '</p>' +
      '</div>';

    var foot = document.createElement('p');
    foot.className = 'ps-anim__footnote';
    foot.textContent =
      'High mode rated 8,000+ PPB. Lab-measured: 8,600 PPB ' +
      '(QTT, report LH2025110014-1, end of 10-min cycle in the sealed ' +
      'cup; drink it fresh, concentration declines after opening).';

    mount.appendChild(root);
    mount.appendChild(foot);

    var els = {
      stage: root.querySelector('.ps-anim__stage'),
      media: root.querySelector('.ps-anim__video'),
      replay: root.querySelector('.ps-anim__replay'),
      count: root.querySelector('.ps-anim__count'),
      countNum: root.querySelector('.ps-anim__count-num'),
      timerNum: root.querySelector('.ps-anim__timer-num'),
      marker: root.querySelector('.ps-anim__marker'),
      badge: root.querySelector('.ps-anim__badge')
    };

    var anim = new PpbAnim(els);

    if (reducedMotion) {
      anim.renderStatic();
      return;
    }

    els.replay.addEventListener('click', function () { anim.replay(); });

    // Start once at >=30% visibility; pause/resume counters based on visibility.
    if ('IntersectionObserver' in window) {
      var started = false;
      var io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var en = entries[i];
          if (en.target !== root) continue;
          anim.inView = en.isIntersecting;
          if (en.isIntersecting && !started && en.intersectionRatio >= 0.3) {
            started = true;
            anim.start();
          } else if (en.isIntersecting) {
            anim.resume();
          } else {
            anim.pause();
          }
        }
      }, { threshold: [0, 0.3] });
      io.observe(root);

      // Separate observer for video: pause when fully offscreen, resume when visible.
      if (els.media && els.media.tagName === 'VIDEO') {
        var videoObserver = new IntersectionObserver(function (entries) {
          for (var j = 0; j < entries.length; j++) {
            var entry = entries[j];
            if (entry.target !== root) continue;
            if (entry.isIntersecting) {
              var playPromise = els.media.play();
              if (playPromise && typeof playPromise.catch === 'function') {
                playPromise.catch(function () {});
              }
            } else {
              els.media.pause();
            }
          }
        }, { threshold: 0 });
        videoObserver.observe(root);
      }
    } else {
      anim.inView = true;
      anim.start();
      if (els.media && els.media.tagName === 'VIDEO') {
        var p = els.media.play();
        if (p && typeof p.catch === 'function') p.catch(function () {});
      }
    }
  }

  /* ------------------------------------------------------------------ *
   * Animation core (counter only; film runs independently)
   * ------------------------------------------------------------------ */
  function PpbAnim(els) {
    this.els = els;
    this.startAt = 0;
    this.progress = 0;
    this.rawT = 0;
    this.running = false;
    this.done = false;
    this.inView = false;
    this.rafId = 0;
    this.markerOn = false;
    this._lastCount = -1;
    this._lastTimer = '';
    this._boundFrame = this.frame.bind(this);
  }

  PpbAnim.prototype.start = function () {
    if (this.running || this.done) return;
    this.startAt = performance.now();
    this.running = true;
    this.rafId = requestAnimationFrame(this._boundFrame);
  };

  PpbAnim.prototype.pause = function () {
    if (this.rafId) { cancelAnimationFrame(this.rafId); this.rafId = 0; }
    if (this.running && !this.done) this._pausedAt = performance.now();
  };

  PpbAnim.prototype.resume = function () {
    if (!this.running || this.rafId) return;
    if (this._pausedAt) { this.startAt += performance.now() - this._pausedAt; this._pausedAt = 0; }
    this.rafId = requestAnimationFrame(this._boundFrame);
  };

  PpbAnim.prototype.replay = function () {
    this.pause();
    this.done = false;
    this.running = false;
    this.markerOn = false;
    this.progress = 0; this.rawT = 0;
    this._lastCount = -1; this._lastTimer = '';
    this.els.replay.hidden = true;
    this.els.badge.classList.remove('is-on');
    this.els.marker.classList.remove('is-hit');
    this.els.count.classList.remove('is-peak');
    this.els.countNum.textContent = '0';
    this.els.timerNum.textContent = '00:00';
    this.start();
  };

  PpbAnim.prototype.frame = function (now) {
    this.rafId = 0;
    var t = (now - this.startAt) / DURATION;
    this.rawT = t;
    this.progress = easeOutQuart(t);

    this.updateReadouts();

    if (t >= 1 && !this.done) {
      this.finish();
    }

    if (!this.done) {
      this.rafId = requestAnimationFrame(this._boundFrame);
    }
  };

  PpbAnim.prototype.finish = function () {
    this.done = true;
    this.els.countNum.textContent = '8,000+';
    this.els.timerNum.textContent = '10:00';
    this.els.count.classList.add('is-peak');
    this.els.badge.classList.add('is-on');
    this.els.replay.hidden = false;
  };

  PpbAnim.prototype.updateReadouts = function () {
    if (this.done) return;
    var ppb = Math.round(PPB_MAX * this.progress);
    if (ppb !== this._lastCount) {
      this._lastCount = ppb;
      this.els.countNum.textContent = ppb.toLocaleString('en-US');
    }
    var secs = Math.round(TIMER_MAX * this.progress);
    var mm = String(Math.floor(secs / 60)); if (mm.length < 2) mm = '0' + mm;
    var ss = String(secs % 60); if (ss.length < 2) ss = '0' + ss;
    var tstr = mm + ':' + ss;
    if (tstr !== this._lastTimer) {
      this._lastTimer = tstr;
      this.els.timerNum.textContent = tstr;
    }
    if (!this.markerOn && ppb >= MARKER_PPB) {
      this.markerOn = true;
      this.els.marker.classList.add('is-hit');
    }
  };

  /* Static final frame for prefers-reduced-motion. */
  PpbAnim.prototype.renderStatic = function () {
    this.progress = 1;
    this.done = true;
    this.els.countNum.textContent = '8,000+';
    this.els.timerNum.textContent = '10:00';
    this.els.count.classList.add('is-peak');
    this.els.marker.classList.add('is-hit');
    this.els.badge.classList.add('is-on');
    this.els.replay.hidden = true;
  };
})();
